import Cookie from 'js-cookie';
import type { ComputedRef } from 'vue';
import { computed, onUnmounted, ref } from 'vue';

type CookieAttributes = NonNullable<Parameters<typeof Cookie.set>[2]>;

interface CookieOptions<T> extends CookieAttributes {
    encode: (input: T) => string;
    decode: (input: string) => T;
}
function tryParse<T>(str: string | undefined, defaultValue: T, decode: (input: string) => T): T {
    try {
        if (str === '' || str === undefined) {
            return defaultValue;
        }

        return decode(str);
    } catch (err) {
        console.error('useCookie parse failed', err);
        return defaultValue;
    }
}

export function useCookie<T>(key: string, defaultValue: T, options?: CookieOptions<T>): [ComputedRef<T>, (newVal: T) => void] {
    const { decode, encode, ...cookieOptions } = options || {};
    const decodeFn = decode ?? JSON.parse;
    const encodeFn = encode ?? JSON.stringify;

    const rawRef = ref(Cookie.get(key));
    const intervalId = window.setInterval(() => {
        rawRef.value = Cookie.get(key);
    }, 250);
    onUnmounted(() => window.clearInterval(intervalId));

    const computedRef = computed(() => tryParse(rawRef.value, defaultValue, decodeFn) as T);
    const setValue = (newValue: T) => {
        const rawValue = encodeFn(newValue);
        rawRef.value = rawValue;
        Cookie.set(key, rawValue, cookieOptions);
    };

    return [computedRef, setValue];
}
